/**
 * interactive-deduction (Phase 3-3, Task 6.5) DeductionPanel 本体 (style dispatcher)。
 *
 * `preset.interactiveDeduction.style` を受けて 3 variant (class-trial / evidence-based /
 * subtle) のいずれかを描画する。`style='none'` は null を返し panel 自体を表示しない
 * (Req 5.4)。未定義 enum は class-trial に fallback + console.warn (Req 5.5)。
 *
 * 内部で useDeduction mutation を呼び、成功 / 失敗 (forcedBadEnding 含む) を親に通知する。
 */

import { useEffect } from "react";
import { useDeduction, type DeductionResponse } from "../../api/hooks/useDeduction";
import type { UIError } from "../../errors/types";
import { DeductionPanelClassTrial } from "./DeductionPanelClassTrial";
import { DeductionPanelEvidenceBased } from "./DeductionPanelEvidenceBased";
import { DeductionPanelSubtle } from "./DeductionPanelSubtle";
import type {
  DeductionPanelContradiction,
  DeductionPanelVariantProps,
  EvidenceFragment,
} from "./types";

export type DeductionStyle =
  | "class-trial"
  | "evidence-based"
  | "subtle"
  | "none";

export type DeductionPanelProps = {
  /** preset.interactiveDeduction.style。none は null 返却 (非表示)。 */
  style: DeductionStyle | string;
  playlineId: string;
  sceneId: string;
  contradictions: DeductionPanelContradiction[];
  memories: EvidenceFragment[];
  attempts: number | null | undefined;
  /** 判定成功時 / forcedBadEnding 時に呼ばれる (nextSceneId を親が Playline 更新に使う) */
  onResolved?: ((result: DeductionResponse) => void) | undefined;
  /** エラー時に呼ばれる (親 UI が banner 表示等で使う) */
  onError?: ((err: UIError) => void) | undefined;
};

const VARIANT_MAP: Record<
  "class-trial" | "evidence-based" | "subtle",
  (props: DeductionPanelVariantProps) => JSX.Element
> = {
  "class-trial": DeductionPanelClassTrial,
  "evidence-based": DeductionPanelEvidenceBased,
  subtle: DeductionPanelSubtle,
};

export function DeductionPanel({
  style,
  playlineId,
  sceneId,
  contradictions,
  memories,
  attempts,
  onResolved,
  onError,
}: DeductionPanelProps): JSX.Element | null {
  const mutation = useDeduction();

  useEffect(() => {
    if (mutation.isSuccess && mutation.data !== undefined) {
      onResolved?.(mutation.data);
    }
  }, [mutation.isSuccess, mutation.data, onResolved]);

  useEffect(() => {
    if (mutation.isError && mutation.error !== null) {
      onError?.(mutation.error);
    }
  }, [mutation.isError, mutation.error, onError]);

  // Req 5.4: style=none は非表示
  if (style === "none") return null;

  // Req 5.5: 未定義 enum は class-trial に fallback + warn
  const resolvedStyle = ((): "class-trial" | "evidence-based" | "subtle" => {
    if (
      style === "class-trial" ||
      style === "evidence-based" ||
      style === "subtle"
    ) {
      return style;
    }
    console.warn(
      `[DeductionPanel] unknown style "${style}", falling back to class-trial`,
    );
    return "class-trial";
  })();

  const Variant = VARIANT_MAP[resolvedStyle];

  return (
    <Variant
      contradictions={contradictions}
      memories={memories}
      attempts={attempts}
      disabled={mutation.isPending}
      onSubmit={({ contradictionId, memoryFragmentKey }) => {
        mutation.mutate({
          playlineId,
          sceneId,
          contradictionId,
          memoryFragmentKey,
        });
      }}
    />
  );
}
